import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface UseAutoFollowOptions {
  /** The scrollable container element. */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /**
   * Selector identifying the element to follow inside the container (e.g.
   * `[data-active-line='true']`). Re-queried after every change to
   * `activeKey` so the consumer can swap which element is "active" by
   * moving the data attribute.
   */
  activeSelector: string;
  /**
   * Value that changes whenever the active element changes (e.g.
   * `${segmentIndex}:${wordIndex}`). Used as the effect dep so autoscroll
   * re-fires when the target moves.
   */
  activeKey: string | number | null;
  /** Skip autoscroll while true (e.g. while the user is searching). */
  paused?: boolean;
}

export interface UseAutoFollowResult {
  /** Whether autoscroll is currently engaged. */
  autoFollow: boolean;
  /** Where the active element is relative to the viewport, when disengaged. */
  jumpDirection: "up" | "down" | null;
  /** Re-engage + bump popKey (rings the active element). For the jump pill. */
  jumpToCurrent: () => void;
  /** Re-engage without the pop animation. For implicit re-engagements like a word click.
   *  `instant: true` makes the next scroll snap with no smooth animation —
   *  click-driven seeks already feel jittery because the active word is
   *  changing at the same time as the scroll animates. */
  enableAutoFollow: (opts?: { instant?: boolean }) => void;
  /** Increments on each `jumpToCurrent()` — use as a key on the active element to re-trigger animations. */
  popKey: number;
}

// Scrolls landing within this window of a wheel/touch/keydown are
// treated as user-initiated. Our own scrollTo() never fires those
// input events, so the smooth-scroll's downstream scroll events fall
// through and don't trigger disengage.
const USER_INPUT_GRACE_MS = 200;

/**
 * "Smart follow" autoscroll: scrolls the active element to ~1/3 from the
 * top of the container whenever it changes. Disengages on any scroll
 * preceded by a wheel/touch/keydown within {@link USER_INPUT_GRACE_MS}.
 *
 * Once disengaged, re-engagement is *explicit* only — the consumer calls
 * `jumpToCurrent` (pill click, with the ring-pulse animation) or
 * `enableAutoFollow` (deliberate navigation like a word click, no pulse).
 * Earlier versions auto-re-engaged via IntersectionObserver when the
 * active element crossed back into view, but that fired mid-scroll as
 * soon as the user scrolled toward current — pure hijack.
 *
 * Used by the transcript panel to follow the playback word and also as the
 * mechanism the search-to-match scroll piggybacks on (by toggling which
 * element matches `activeSelector`).
 */
export function useAutoFollow({
  scrollContainerRef,
  activeSelector,
  activeKey,
  paused = false,
}: UseAutoFollowOptions): UseAutoFollowResult {
  const [autoFollow, setAutoFollow] = useState(true);
  const userInputAt = useRef(0);
  // Scrolls firing before this timestamp use "auto" (instant) instead of
  // "smooth". A single click can trigger multiple scrolls (the immediate
  // snap to the clicked word, then a catch-up scroll when pendingClick
  // clears and playback has already advanced) — covering the whole window
  // is what stops the second one from animating smoothly.
  //
  // Initialized to "now + window" so the first scroll-to-current after
  // mount (e.g. when the user toggles the transcript panel open) snaps
  // instead of smooth-scrolling from the top.
  const INSTANT_WINDOW_MS = 400;
  const instantUntil = useRef(Date.now() + INSTANT_WINDOW_MS);

  // Mark wheel/touch/keydown on the container. Programmatic scrolls
  // (our own scrollTo) don't fire any of these, so the downstream
  // scroll events look "scroll without prior input" and are ignored.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const mark = () => {
      userInputAt.current = Date.now();
    };
    container.addEventListener("wheel", mark, { passive: true });
    container.addEventListener("touchstart", mark, { passive: true });
    container.addEventListener("keydown", mark);
    return () => {
      container.removeEventListener("wheel", mark);
      container.removeEventListener("touchstart", mark);
      container.removeEventListener("keydown", mark);
    };
  }, [scrollContainerRef]);

  // Disengage on any scroll preceded by a recent user input event.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      if (!autoFollow) return;
      if (Date.now() - userInputAt.current > USER_INPUT_GRACE_MS) return;
      setAutoFollow(false);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [autoFollow, scrollContainerRef]);

  // While engaged, scroll the active element so it sits ~1/3 down the
  // container. Leaves the bottom 2/3 as lookahead.
  useEffect(() => {
    if (!autoFollow || paused) return;
    const container = scrollContainerRef.current;
    const active = container?.querySelector(activeSelector) as
      | HTMLElement
      | null;
    if (!container || !active) return;
    const containerRect = container.getBoundingClientRect();
    const lineRect = active.getBoundingClientRect();
    const targetTopOffset = containerRect.height / 3;
    const delta = lineRect.top - containerRect.top - targetTopOffset;
    if (Math.abs(delta) < 1) return;
    const behavior: ScrollBehavior =
      Date.now() < instantUntil.current ? "auto" : "smooth";
    container.scrollTo({
      top: container.scrollTop + delta,
      behavior,
    });
  }, [autoFollow, paused, activeKey, scrollContainerRef, activeSelector]);

  // Where is the active element relative to viewport when disengaged?
  // Used by the consumer to render a directional jump pill.
  const [jumpDirection, setJumpDirection] = useState<"up" | "down" | null>(
    null,
  );
  useEffect(() => {
    if (autoFollow) {
      setJumpDirection(null);
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) return;
    const recompute = () => {
      const active = container.querySelector(activeSelector);
      if (!active) {
        setJumpDirection(null);
        return;
      }
      const lineRect = active.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (lineRect.top >= containerRect.bottom) setJumpDirection("down");
      else if (lineRect.bottom <= containerRect.top) setJumpDirection("up");
      else setJumpDirection(null);
    };
    recompute();
    container.addEventListener("scroll", recompute, { passive: true });
    return () => container.removeEventListener("scroll", recompute);
  }, [autoFollow, activeKey, scrollContainerRef, activeSelector]);

  // Pop key increments on each jumpToCurrent — consumers use it as a key
  // on the active element to remount + re-trigger framer-motion animations.
  const [popKey, setPopKey] = useState(0);

  const jumpToCurrent = useCallback(() => {
    instantUntil.current = Date.now() + INSTANT_WINDOW_MS;
    setAutoFollow(true);
    setPopKey((k) => k + 1);
  }, []);

  const enableAutoFollow = useCallback(
    (opts?: { instant?: boolean }) => {
      if (opts?.instant) instantUntil.current = Date.now() + INSTANT_WINDOW_MS;
      setAutoFollow(true);
    },
    [],
  );

  return {
    autoFollow,
    jumpDirection,
    jumpToCurrent,
    enableAutoFollow,
    popKey,
  };
}
